import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Grid,
  InputAdornment,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PercentIcon from '@mui/icons-material/Percent';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  REVIEW_ACCENT,
  getReviewPalette,
  reviewIconBadgeSx,
  reviewSliderSx,
  reviewSwitchSx,
  reviewTaxModeToggleSx,
  reviewToggleGroupSx,
} from './reviewStepStyles';
import { formatDiscountLabel } from '../../utils/reviewDiscountUtils';

const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

function CurrencyAmountField({
  value,
  onChange,
  maxAmount,
  isDark,
  currencySymbol = '₹',
  placeholder = '0.00',
  width = 120,
}) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const reviewPalette = getReviewPalette(isDark);

  useEffect(() => {
    if (!focused) {
      setDraft(value > 0 ? String(roundCurrency(value)) : '');
    }
  }, [value, focused]);

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseFloat(draft);
    const cappedMax = maxAmount != null ? maxAmount : Number.POSITIVE_INFINITY;
    const next = Number.isFinite(parsed)
      ? roundCurrency(Math.min(Math.max(parsed, 0), cappedMax))
      : 0;
    onChange(next);
    setDraft(next > 0 ? String(next) : '');
  };

  return (
    <TextField
      size="small"
      value={focused ? draft : (value > 0 ? roundCurrency(value).toFixed(2) : '')}
      onFocus={() => {
        setFocused(true);
        setDraft(value > 0 ? String(roundCurrency(value)) : '');
      }}
      onChange={(e) => setDraft(e.target.value.replace(/[^\d.]/g, ''))}
      onBlur={handleBlur}
      placeholder={placeholder}
      inputMode="decimal"
      InputProps={{
        startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
      }}
      sx={{
        width: { xs: 108, sm: width },
        '& .MuiOutlinedInput-root': {
          height: 34,
          fontSize: '0.8125rem',
          backgroundColor: isDark ? alpha('#fff', 0.04) : '#fff',
        },
        '& .MuiInputLabel-root': { color: reviewPalette.textMuted },
      }}
    />
  );
}

function PricingPanel({ active, isDark, icon, title, headerAction, children }) {
  const reviewPalette = getReviewPalette(isDark);
  const hasBody = React.Children.count(children) > 0;
  return (
    <Box
      sx={{
        height: '100%',
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: 2,
        border: `1px solid ${active ? alpha(REVIEW_ACCENT, isDark ? 0.45 : 0.35) : reviewPalette.lineSubtle}`,
        background: active
          ? `linear-gradient(145deg, ${alpha(REVIEW_ACCENT, isDark ? 0.12 : 0.08)} 0%, ${alpha('#fff', isDark ? 0.03 : 0.9)} 100%)`
          : isDark
            ? alpha('#fff', 0.02)
            : alpha('#f8fafc', 0.9),
        boxShadow: active ? `0 8px 24px ${alpha(REVIEW_ACCENT, isDark ? 0.12 : 0.08)}` : 'none',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: hasBody ? 1 : 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={reviewIconBadgeSx(active, isDark, 32)}>{icon}</Box>
          <Typography sx={{ color: reviewPalette.text, fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '0.02em' }}>
            {title}
          </Typography>
        </Stack>
        {headerAction}
      </Stack>
      {children}
    </Box>
  );
}

export default function ReviewTaxSection({
  hasTaxRules,
  gstIncluded,
  taxInclusive,
  gstPercentage = 0,
  autoGstEnabled = false,
  onGstIncludedChange = () => {},
  onTaxInclusiveChange = () => {},
  allowDiscount = false,
  onAllowDiscountChange = () => {},
  discountMode = 'percentage',
  onDiscountModeChange = () => {},
  discountPercentage = 5,
  onDiscountPercentageChange = () => {},
  discountAmount = 0,
  onDiscountAmountChange = () => {},
  maxDiscountAmount = 0,
  isDark = false,
  currencySymbol = '₹',
  flat = false,
}) {
  const reviewPalette = getReviewPalette(isDark);
  const slabRate = Number(gstPercentage) > 0 ? Number(gstPercentage) : null;
  const discountSummary = formatDiscountLabel({ discountMode, discountPercentage, discountAmount });
  const sliderDiscountValue = Math.min(50, Math.max(5, Number(discountPercentage) || 5));

  const handleDiscountModeChange = (_, value) => {
    if (!value) return;
    if (value === 'amount' && maxDiscountAmount > 0) {
      const fromPct = roundCurrency(maxDiscountAmount * (discountPercentage / 100));
      onDiscountAmountChange(fromPct > 0 ? fromPct : roundCurrency(maxDiscountAmount * 0.05));
    }
    if (value === 'percentage' && maxDiscountAmount > 0 && discountAmount > 0) {
      const pct = roundCurrency((discountAmount / maxDiscountAmount) * 100);
      onDiscountPercentageChange(Math.min(50, Math.max(5, pct || 5)));
    }
    onDiscountModeChange(value);
  };

  return (
    <Grid container spacing={flat ? 1.25 : 1.5}>
      {hasTaxRules ? (
        <Grid item xs={12} md={6}>
          <PricingPanel
            active={gstIncluded}
            isDark={isDark}
            icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 17 }} />}
            title="GST & Tax"
            headerAction={autoGstEnabled ? null : (
              <Switch
                checked={gstIncluded}
                onChange={(e) => onGstIncludedChange(e.target.checked)}
                size="small"
                sx={reviewSwitchSx()}
                inputProps={{ 'aria-label': 'Enable GST' }}
              />
            )}
          >
            {gstIncluded ? (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={taxInclusive ? 'inclusive' : 'exclusive'}
                  onChange={(_, value) => {
                    if (value != null) onTaxInclusiveChange(value === 'inclusive');
                  }}
                  sx={{
                    ...reviewToggleGroupSx(isDark, reviewPalette.border),
                    ...reviewTaxModeToggleSx(isDark, true),
                  }}
                >
                  <ToggleButton value="inclusive">Inclusive</ToggleButton>
                  <ToggleButton value="exclusive">Exclusive</ToggleButton>
                </ToggleButtonGroup>
                {slabRate != null ? (
                  <Chip
                    size="small"
                    label={`${slabRate}% GST`}
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      backgroundColor: isDark ? alpha(REVIEW_ACCENT, 0.14) : alpha(REVIEW_ACCENT, 0.1),
                      color: isDark ? REVIEW_ACCENT : '#0f766e',
                    }}
                  />
                ) : null}
              </Stack>
            ) : null}
          </PricingPanel>
        </Grid>
      ) : null}

      <Grid item xs={12} md={hasTaxRules ? 6 : 12}>
        <PricingPanel
          active={allowDiscount}
          isDark={isDark}
          icon={<LocalOfferOutlinedIcon sx={{ fontSize: 17 }} />}
          title="Discount"
          headerAction={(
            <Switch
              checked={allowDiscount}
              onChange={(e) => onAllowDiscountChange(e.target.checked)}
              size="small"
              sx={reviewSwitchSx()}
              inputProps={{ 'aria-label': 'Allow discount' }}
            />
          )}
        >
          {allowDiscount ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={discountMode}
                onChange={handleDiscountModeChange}
                sx={{
                  ...reviewToggleGroupSx(isDark, reviewPalette.border),
                  '& .MuiToggleButton-root': {
                    minWidth: 38,
                    px: 1,
                    py: 0.35,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  },
                }}
              >
                <ToggleButton value="percentage" aria-label="Discount percentage">
                  <PercentIcon sx={{ fontSize: 16 }} />
                </ToggleButton>
                <ToggleButton value="amount" aria-label="Discount amount">
                  ₹
                </ToggleButton>
              </ToggleButtonGroup>

              {discountMode === 'percentage' ? (
                <Box sx={{ flex: '1 1 120px', minWidth: 110, maxWidth: 220 }}>
                  <Slider
                    size="small"
                    value={sliderDiscountValue}
                    onChange={(_, newValue) => onDiscountPercentageChange(newValue)}
                    min={5}
                    max={50}
                    step={1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}%`}
                    sx={reviewSliderSx()}
                  />
                </Box>
              ) : (
                <CurrencyAmountField
                  value={discountAmount}
                  onChange={onDiscountAmountChange}
                  maxAmount={maxDiscountAmount}
                  isDark={isDark}
                  currencySymbol={currencySymbol}
                />
              )}

              <Chip
                size="small"
                label={discountSummary}
                sx={{
                  height: 24,
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  background: `linear-gradient(135deg, ${REVIEW_ACCENT} 0%, #3ab896 100%)`,
                  color: '#000',
                }}
              />
            </Stack>
          ) : null}
        </PricingPanel>
      </Grid>
    </Grid>
  );
}
